import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, Trash2, Bot, User as UserIcon, Copy, RefreshCw, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function renderMarkdown(text) {
  if (!text) return "";
  // Simple markdown: **bold**, • lists, line breaks
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code class='bg-slate-200 px-1 rounded text-xs'>$1</code>")
    .split("\n")
    .map(l => l.startsWith("• ") || l.startsWith("- ") ? `<div class='ml-3'>• ${l.slice(2)}</div>` : `<div>${l || "&nbsp;"}</div>`)
    .join("");
}

export default function AiAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/ai-assistant/history?limit=30`);
      const msgs = [];
      r.data.forEach(item => {
        msgs.push({ role: "user", text: item.question, at: item.created_at });
        msgs.push({ role: "assistant", text: item.answer, at: item.created_at });
      });
      if (msgs.length > 0) {
        setMessages(msgs);
        setSessionId(r.data[r.data.length - 1].session_id);
      }
    } catch (e) { /* silent */ }
  };

  const loadSuggestions = async () => {
    try {
      const r = await axios.get(`${API}/ai-assistant/suggestions`);
      setSuggestions(r.data.suggestions || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    loadHistory();
    loadSuggestions();
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text, extraPayload = {}) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    if (!extraPayload.confirm_destructive) {
      const userMsg = { role: "user", text: q, at: new Date().toISOString() };
      setMessages(prev => [...prev, userMsg]);
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ai-assistant/ask`, { question: q, session_id: sessionId, ...extraPayload });
      setSessionId(r.data.session_id);
      const aiMsg = {
        role: "assistant",
        text: r.data.answer,
        facts: r.data.snapshot_facts,
        actions: r.data.actions_executed || [],
        pending: r.data.pending_confirm,
        at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erro ao consultar o assistente";
      setMessages(prev => [...prev, { role: "assistant", text: `⚠️ ${detail}`, at: new Date().toISOString(), error: true }]);
      toast.error(detail);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const confirmPending = async (pending) => {
    await send(`Confirmado: ${pending.tool}`, { confirm_destructive: true, pending_action: pending });
  };

  const rejectPending = () => {
    setMessages(prev => [...prev, { role: "assistant", text: "Acção cancelada pelo utilizador.", at: new Date().toISOString() }]);
  };

  const clearHistory = async () => {
    if (!window.confirm("Limpar todo o histórico deste utilizador?")) return;
    try {
      await axios.delete(`${API}/ai-assistant/history`);
      setMessages([]);
      setSessionId(null);
      toast.success("Histórico limpo");
    } catch (e) { toast.error("Erro"); }
  };

  const copyText = (t) => {
    navigator.clipboard.writeText(t);
    toast.success("Copiado");
  };

  return (
    <div className="space-y-4" data-testid="ai-assistant-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-orange-500" /> Assistente EP3D
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pergunta em linguagem natural sobre a produção — a IA responde com dados em tempo real da tua fábrica.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setMessages([]); setSessionId(null); }} data-testid="new-session-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Nova sessão
          </Button>
          <Button variant="outline" size="sm" onClick={clearHistory} className="text-red-600" data-testid="clear-history-btn">
            <Trash2 className="h-4 w-4 mr-1" /> Limpar histórico
          </Button>
        </div>
      </div>

      {/* Suggestions */}
      {messages.length === 0 && suggestions.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold text-slate-700 mb-2">Sugestões:</div>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => send(s)}
                className="text-xs px-3 py-1.5 rounded-full border border-orange-300 text-orange-700 hover:bg-orange-50 transition"
                data-testid={`suggestion-${i}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Chat area */}
      <div className="bg-white border rounded-lg shadow-sm flex flex-col" style={{ height: "60vh" }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center text-slate-400">
              <Bot className="h-20 w-20 mb-3 opacity-40" />
              <div className="text-lg font-semibold">Pergunta-me algo sobre a fábrica</div>
              <div className="text-xs mt-1 max-w-xs">Exemplos: quantas peças hoje, ordens atrasadas, quem está em serviço, motivos de defeito...</div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "assistant" && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white">
                  <Bot className="h-4 w-4" />
                </div>
              )}
              <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-blue-600 text-white" : m.error ? "bg-red-50 border border-red-200 text-red-900" : "bg-slate-100 text-slate-900"}`}>
                <div
                  className="text-sm leading-relaxed whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }}
                />
                {m.facts && (
                  <div className="mt-2 pt-2 border-t border-slate-300 grid grid-cols-3 gap-2 text-[10px] text-slate-600">
                    <div><b>{m.facts.orders_active}</b> ordens ativas</div>
                    <div><b>{m.facts.orders_overdue}</b> atrasadas</div>
                    <div><b>{m.facts.pieces_today}</b> peças hoje</div>
                    <div><b>{m.facts.defects_today}</b> defeitos</div>
                    <div><b>{m.facts.staff_active}</b> em serviço</div>
                    <div><b>{m.facts.machines_stopped}</b> máq. paradas</div>
                  </div>
                )}
                {m.role === "assistant" && !m.error && (
                  <button onClick={() => copyText(m.text)} className="mt-2 text-[10px] text-slate-500 hover:text-slate-800 flex items-center gap-1">
                    <Copy className="h-3 w-3" /> Copiar
                  </button>
                )}
                {/* Executed actions badges */}
                {m.actions && m.actions.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-300 space-y-1">
                    {m.actions.map((a, ai) => (
                      <div key={ai} className="flex items-center gap-1 text-[10px] text-slate-700">
                        <span className={`px-1.5 py-0.5 rounded font-mono ${a.result?.ok ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                          {a.result?.ok ? "✓" : "✗"} {a.tool || a.action}
                        </span>
                        <span className="opacity-70 truncate">{a.result?.message || a.result?.error || ""}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Destructive confirmation buttons */}
                {m.pending && (
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" onClick={() => confirmPending(m.pending)} className="bg-red-600 hover:bg-red-700" data-testid="confirm-destructive">
                      <ShieldAlert className="h-3.5 w-3.5 mr-1" /> Sim, executar
                    </Button>
                    <Button size="sm" variant="outline" onClick={rejectPending} data-testid="reject-destructive">
                      Cancelar
                    </Button>
                  </div>
                )}
              </div>
              {m.role === "user" && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white">
                  <UserIcon className="h-4 w-4" />
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex gap-3 justify-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white">
                <Bot className="h-4 w-4" />
              </div>
              <div className="bg-slate-100 rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  A consultar dados da fábrica...
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t p-3 flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Pergunta ao assistente... (ex: quantas ordens estão atrasadas?)"
            disabled={loading}
            className="flex-1"
            data-testid="ai-input"
          />
          <Button onClick={() => send()} disabled={loading || !input.trim()} className="bg-orange-500 hover:bg-orange-600" data-testid="ai-send-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <div className="text-xs text-center text-slate-500">
        As respostas usam Gemini 3 Flash + snapshot em tempo real da BD. Precisão dependente da qualidade dos dados registados.
      </div>
    </div>
  );
}
